import React, { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { ArrowDownTrayIcon, PencilSquareIcon, MusicalNoteIcon } from '@heroicons/react/24/outline';
import { audioToVideoApi, AudioToVideoJob } from '@/services/audioToVideoApi';
import YouTubePublishModal from '@/components/Video/YouTubePublishModal';
import SocialPublishModal from '@/components/Video/SocialPublishModal';

interface PublishResultViewProps {
  job: AudioToVideoJob;
  onStartOver: () => void;
  onEditAudio: () => void;
  onEditTranscript: () => void;
}

// Once rendering completes, the backend has already created a VideoUpload
// doc (job.result.videoUploadFileId) — so publishing reuses the EXISTING
// YouTube/social publish modals unchanged, parameterized with
// sourceType="upload", rather than building new publish UI.
const PublishResultView: React.FC<PublishResultViewProps> = ({ job, onStartOver, onEditAudio, onEditTranscript }) => {
  const [showYouTube, setShowYouTube] = useState(false);
  const [showSocial, setShowSocial] = useState(false);

  const { data } = useQuery({
    queryKey: ['audio-to-video-url', job.jobId],
    queryFn: () => audioToVideoApi.getVideoUrl(job.jobId),
  });

  const videoUploadFileId = job.result?.videoUploadFileId;

  return (
    <div className="space-y-6">
      <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
        {data?.url ? (
          <video controls src={data.url} className="w-full rounded-md max-h-[480px] bg-black" />
        ) : (
          <div className="flex items-center justify-center h-48">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        )}
        {data?.url && (
          <a
            href={data.url}
            download
            className="mt-3 inline-flex items-center gap-1.5 text-sm font-medium text-blue-600 dark:text-blue-400 hover:underline"
          >
            <ArrowDownTrayIcon className="h-4 w-4" />
            Download video
          </a>
        )}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <button
          onClick={() => setShowYouTube(true)}
          disabled={!videoUploadFileId}
          className="px-4 py-2 text-sm font-medium text-white bg-red-600 hover:bg-red-700 rounded-md disabled:opacity-50"
        >
          Publish to YouTube
        </button>
        <button
          onClick={() => setShowSocial(true)}
          disabled={!videoUploadFileId}
          className="px-4 py-2 text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 rounded-md disabled:opacity-50"
        >
          Publish to other channels
        </button>
        <button
          onClick={onEditAudio}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <MusicalNoteIcon className="h-4 w-4" />
          Edit audio
        </button>
        <button
          onClick={onEditTranscript}
          className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <PencilSquareIcon className="h-4 w-4" />
          Edit transcript
        </button>
        <button onClick={onStartOver} className="px-4 py-2 text-sm font-medium text-gray-600 dark:text-gray-300">
          Create another video
        </button>
      </div>
      <p className="text-xs text-gray-400 dark:text-gray-500">
        Editing audio or transcript generates a new video — this one stays as-is.
      </p>

      {showYouTube && videoUploadFileId && (
        <YouTubePublishModal
          sourceType="upload"
          sourceId={videoUploadFileId}
          defaultTitle={job.title}
          onClose={() => setShowYouTube(false)}
        />
      )}

      {showSocial && videoUploadFileId && (
        <SocialPublishModal
          sourceType="upload"
          sourceId={videoUploadFileId}
          onClose={() => setShowSocial(false)}
          onPublished={() => setShowSocial(false)}
        />
      )}
    </div>
  );
};

export default PublishResultView;
